import { ENTITIES_KEY_FORM } from '../index';

import { getForm } from '../selectors';

// Constants
export const SESSIONS_STEP_CONFIG = {
    ENABLED: 'enabled',
    LABEL: 'label',
    SETTINGS: 'settings',
    BACK_BUTTON_LABEL: 'backButtonCopy',
    NEXT_BUTTON_LABEL: 'nextButtonCopy',
    SUBMIT_BUTTON_LABEL: 'submitButtonCopy',
    COLLAPSIBLE_LABEL: 'collapsibleCopy',
    WAITLIST_LABEL: 'waitlistCopy',
    SPEAKERS_ENABLED: 'showSpeakers',
    TAGS_ENABLED: 'showTags',
    DESCRIPTION_ENABLED: 'showDescription',
};

// Action types
const TOGGLE_SESSIONS_STEPS = 'entities/forms/TOGGLE_SESSIONS_STEPS';
const SET_SESSIONS_STEPS_LABEL = 'entities/forms/SET_SESSIONS_STEPS_LABEL';
const SET_SESSIONS_STEPS_BACK_BUTTON_LABEL = 'entities/forms/SET_SESSIONS_STEPS_BACK_BUTTON_LABEL';
const SET_SESSIONS_STEPS_NEXT_BUTTON_LABEL = 'entities/forms/SET_SESSIONS_STEPS_NEXT_BUTTON_LABEL';
const SET_SESSIONS_STEPS_SUBMIT_BUTTON_LABEL = 'entities/forms/SET_SESSIONS_STEPS_SUBMIT_BUTTON_LABEL';
const SET_SESSIONS_STEPS_SETTINGS_COLLAPSIBLE_LABEL = 'entities/forms/SET_SESSIONS_STEPS_SETTINGS_COLLAPSIBLE_LABEL';
const SET_SESSIONS_STEPS_SETTINGS_WAITLIST_LABEL = 'entities/forms/SET_SESSIONS_STEPS_SETTINGS_WAITLIST_LABEL';
const SET_SESSIONS_STEPS_SETTINGS_SPEAKERS = 'entities/forms/SET_SESSIONS_STEPS_SETTINGS_SPEAKERS';
const SET_SESSIONS_STEPS_SETTINGS_TAGS = 'entities/forms/SET_SESSIONS_STEPS_SETTINGS_TAGS';
const SET_SESSIONS_STEPS_SETTINGS_DESCRIPTION = 'entities/forms/SET_SESSIONS_STEPS_SETTINGS_DESCRIPTION';

// Actions

const setSessionsSteps = (type, formId, sessions) => ({
    type,
    payload: {
        entities: {
            [ENTITIES_KEY_FORM]: {
                [formId]: {
                    sessions,
                },
            },
        },
    },
});

export const toggleSessionsSteps = (formId, isSessionsStepEnabled) =>
    setSessionsSteps(TOGGLE_SESSIONS_STEPS, formId, { [SESSIONS_STEP_CONFIG.ENABLED]: isSessionsStepEnabled });

export const setSessionsStepsLabel = (formId, label) =>
    setSessionsSteps(SET_SESSIONS_STEPS_LABEL, formId, { [SESSIONS_STEP_CONFIG.LABEL]: label });

export const setSessionsStepsBackButtonLabel = (formId, label) =>
    setSessionsSteps(SET_SESSIONS_STEPS_BACK_BUTTON_LABEL, formId, { [SESSIONS_STEP_CONFIG.BACK_BUTTON_LABEL]: label });

export const setSessionsStepsNextButtonLabel = (formId, label) =>
    setSessionsSteps(SET_SESSIONS_STEPS_NEXT_BUTTON_LABEL, formId, { [SESSIONS_STEP_CONFIG.NEXT_BUTTON_LABEL]: label });

export const setSessionsStepsSubmitButtonLabel = (formId, label) =>
    setSessionsSteps(SET_SESSIONS_STEPS_SUBMIT_BUTTON_LABEL, formId, {
        [SESSIONS_STEP_CONFIG.SUBMIT_BUTTON_LABEL]: label,
    });

export const setSessionStepsSettingsCollapsibleLabel = (formId, label) =>
    setSessionsSteps(SET_SESSIONS_STEPS_SETTINGS_COLLAPSIBLE_LABEL, formId, {
        [SESSIONS_STEP_CONFIG.SETTINGS]: {
            [SESSIONS_STEP_CONFIG.COLLAPSIBLE_LABEL]: label,
        },
    });

export const setSessionStepsSettingsWaitlistLabel = (formId, label) =>
    setSessionsSteps(SET_SESSIONS_STEPS_SETTINGS_WAITLIST_LABEL, formId, {
        [SESSIONS_STEP_CONFIG.SETTINGS]: {
            [SESSIONS_STEP_CONFIG.WAITLIST_LABEL]: label,
        },
    });

export const toggleSessionStepsSettingsSpeakers = (formId, isEnabled) =>
    setSessionsSteps(SET_SESSIONS_STEPS_SETTINGS_SPEAKERS, formId, {
        [SESSIONS_STEP_CONFIG.SETTINGS]: {
            [SESSIONS_STEP_CONFIG.SPEAKERS_ENABLED]: isEnabled,
        },
    });

export const toggleSessionStepsSettingsTags = (formId, isEnabled) =>
    setSessionsSteps(SET_SESSIONS_STEPS_SETTINGS_TAGS, formId, {
        [SESSIONS_STEP_CONFIG.SETTINGS]: {
            [SESSIONS_STEP_CONFIG.TAGS_ENABLED]: isEnabled,
        },
    });

export const toggleSessionStepsSettingsDescription = (formId, isEnabled) =>
    setSessionsSteps(SET_SESSIONS_STEPS_SETTINGS_DESCRIPTION, formId, {
        [SESSIONS_STEP_CONFIG.SETTINGS]: {
            [SESSIONS_STEP_CONFIG.DESCRIPTION_ENABLED]: isEnabled,
        },
    });

// Selectors
const getFormSessionSteps = (state, formId) => {
    const form = getForm(state, formId);

    return form && form.sessions;
};

export const getFormSessionStepsEnabled = (state, formId) => {
    const sessions = getFormSessionSteps(state, formId);

    return sessions?.[SESSIONS_STEP_CONFIG.ENABLED];
};

export const getFormSessionStepsLabel = (state, formId) => {
    const sessions = getFormSessionSteps(state, formId);

    if (sessions?.[SESSIONS_STEP_CONFIG.LABEL] === undefined) {
        return `Select the sessions you'd like to attend`;
    }

    return sessions?.[SESSIONS_STEP_CONFIG.LABEL];
};

export const getFormSessionStepsBackButtonLabel = (state, formId) => {
    const sessions = getFormSessionSteps(state, formId);

    if (sessions?.[SESSIONS_STEP_CONFIG.BACK_BUTTON_LABEL] === undefined) {
        return 'Back';
    }

    return sessions?.[SESSIONS_STEP_CONFIG.BACK_BUTTON_LABEL];
};

export const getFormSessionStepsNextButtonLabel = (state, formId) => {
    const sessions = getFormSessionSteps(state, formId);

    if (sessions?.[SESSIONS_STEP_CONFIG.NEXT_BUTTON_LABEL] === undefined) {
        return 'Next';
    }

    return sessions?.[SESSIONS_STEP_CONFIG.NEXT_BUTTON_LABEL];
};

export const getFormSessionStepsSubmitButtonLabel = (state, formId) => {
    const sessions = getFormSessionSteps(state, formId);

    if (sessions?.[SESSIONS_STEP_CONFIG.SUBMIT_BUTTON_LABEL] === undefined) {
        return 'Submit';
    }

    return sessions?.[SESSIONS_STEP_CONFIG.SUBMIT_BUTTON_LABEL];
};

export const getFormSessionStepsSettingsCollapsibleLabel = (state, formId) => {
    const { SETTINGS, COLLAPSIBLE_LABEL } = SESSIONS_STEP_CONFIG;
    const sessions = getFormSessionSteps(state, formId);

    if (sessions?.[SETTINGS]?.[COLLAPSIBLE_LABEL] === undefined) {
        return 'More details';
    }

    return sessions?.[SETTINGS]?.[COLLAPSIBLE_LABEL];
};

export const getFormSessionStepsSettingsWaitlistLabel = (state, formId) => {
    const { SETTINGS, WAITLIST_LABEL } = SESSIONS_STEP_CONFIG;
    const sessions = getFormSessionSteps(state, formId);

    if (sessions?.[SETTINGS]?.[WAITLIST_LABEL] === undefined) {
        return 'Waitlist';
    }

    return sessions?.[SETTINGS]?.[WAITLIST_LABEL];
};

export const getFormSessionStepsSettingsSpeakers = (state, formId) => {
    const { SETTINGS, SPEAKERS_ENABLED } = SESSIONS_STEP_CONFIG;
    const sessions = getFormSessionSteps(state, formId);

    return sessions?.[SETTINGS]?.[SPEAKERS_ENABLED];
};

export const getFormSessionStepsSettingsTags = (state, formId) => {
    const { SETTINGS, TAGS_ENABLED } = SESSIONS_STEP_CONFIG;
    const sessions = getFormSessionSteps(state, formId);

    return sessions?.[SETTINGS]?.[TAGS_ENABLED];
};

export const getFormSessionStepsSettingsDescription = (state, formId) => {
    const { SETTINGS, DESCRIPTION_ENABLED } = SESSIONS_STEP_CONFIG;
    const sessions = getFormSessionSteps(state, formId);

    return sessions?.[SETTINGS]?.[DESCRIPTION_ENABLED];
};
